import React, { useState, useEffect, useCallback } from 'react';
import { Info, PlusCircle, Trash2, CheckCircle } from 'lucide-react';
import { ProfessionalLayout } from '@/modules/professional/layouts/ProfessionalLayout';
import { useRoleStore } from '@/core/application/state/useRoleStore';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

// ─── Constantes ───────────────────────────────────────────────────────────────
const MAX_TITULO = 50;
const MAX_CONTENIDO = 200;
const MAX_ITEMS = 6;

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface FaqItem {
  id: number;
  titulo_opcion: string;
  contenido_respuesta: string;
  precio_sugerido: string;
  moneda: 'USD' | 'EUR';
  dispara_agendamiento: boolean;
  isNew?: boolean;
  isDirty?: boolean;
}

const EMPTY_ITEM = (): Omit<FaqItem, 'id'> => ({
  titulo_opcion: '',
  contenido_respuesta: '',
  precio_sugerido: '',
  moneda: 'USD',
  dispara_agendamiento: false,
  isNew: true,
  isDirty: true,
});

// ─── CharCount ────────────────────────────────────────────────────────────────
function CharCount({ current, max }: { readonly current: number; readonly max: number }) {
  const remaining = max - current;
  let color = '#94A3B8';
  if (remaining <= 20) color = '#D97706';
  if (remaining <= 0) color = '#EF4444';
  return <span style={{ fontSize: '12px', fontWeight: 600, color }}>{remaining}</span>;
}

// ─── FaqEditor ────────────────────────────────────────────────────────────────
function FaqEditor({ item, index, onChange, onDelete, saving }: {
  readonly item: FaqItem;
  readonly index: number;
  readonly onChange: (updated: FaqItem) => void;
  readonly onDelete: () => void;
  readonly saving: boolean;
}) {
  const theme = useRoleStore(s => s.getTheme());
  const update = (patch: Partial<FaqItem>) => onChange({ ...item, ...patch, isDirty: true });

  return (
    <div style={{ ...styles.card, borderColor: '#E2E8F0' }}>
      <div style={styles.cardHeader}>
        <div style={{ ...styles.cardIndexBadge, backgroundColor: theme.primary + '15' }}>
          <span style={{ ...styles.cardIndexText, color: theme.primary }}>{index + 1}</span>
        </div>
        <span style={{ ...styles.cardHeaderLabel, color: theme.text }}>Tarjeta de información</span>
        <button style={styles.deleteBtn} onClick={onDelete} disabled={saving}>
          <Trash2 size={18} color="#EF4444" />
        </button>
      </div>

      {/* Título */}
      <div style={styles.fieldBlock}>
        <div style={styles.fieldLabelRow}>
          <span style={{ ...styles.fieldLabel, color: theme.textSecondary }}>Título visible</span>
          <CharCount current={item.titulo_opcion.length} max={MAX_TITULO} />
        </div>
        <input
          style={{ ...styles.input, color: theme.text, borderColor: '#CBD5E1' }}
          placeholder="Ej: Servicios que ofrezco"
          value={item.titulo_opcion}
          onChange={e => update({ titulo_opcion: e.target.value.slice(0, MAX_TITULO) })}
          maxLength={MAX_TITULO}
        />
        <span style={{ ...styles.fieldHint, color: theme.textSecondary }}>
          Aparece como etiqueta en tu perfil. Sé directo y descriptivo.
        </span>
      </div>

      {/* Contenido */}
      <div style={styles.fieldBlock}>
        <div style={styles.fieldLabelRow}>
          <span style={{ ...styles.fieldLabel, color: theme.textSecondary }}>Descripción</span>
          <CharCount current={item.contenido_respuesta.length} max={MAX_CONTENIDO} />
        </div>
        <textarea
          style={{ ...styles.input, ...styles.inputMultiline, color: theme.text, borderColor: '#CBD5E1' }}
          placeholder="Describe brevemente esta categoría en 1 o 2 oraciones."
          value={item.contenido_respuesta}
          onChange={e => update({ contenido_respuesta: e.target.value.slice(0, MAX_CONTENIDO) })}
          maxLength={MAX_CONTENIDO}
          rows={3}
        />
        <span style={{ ...styles.fieldHint, color: theme.textSecondary }}>
          Máximo {MAX_CONTENIDO} caracteres. El paciente debe poder leerlo en menos de 5 segundos.
        </span>
      </div>

      {/* Precio */}
      <div style={styles.fieldBlock}>
        <div style={styles.fieldLabelRow}>
          <span style={{ ...styles.fieldLabel, color: theme.textSecondary }}>Tarifa de referencia</span>
          <span style={{ ...styles.fieldOptional, color: theme.textSecondary }}>Opcional</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            style={{ ...styles.input, width: '90px', color: theme.text, borderColor: '#CBD5E1', outline: 'none' }}
            value={item.moneda}
            onChange={e => update({ moneda: e.target.value as 'USD' | 'EUR' })}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
          <input
            style={{ ...styles.input, ...styles.inputPrice, color: theme.text, borderColor: '#CBD5E1', flex: 1 }}
            placeholder="0,00"
            value={item.precio_sugerido}
            onChange={e => {
              const clean = e.target.value.replace(/[^0-9,]/g, '').replace(/(,.*?),.*/, '$1');
              update({ precio_sugerido: clean });
            }}
          />
        </div>
        <span style={{ ...styles.fieldHint, color: theme.textSecondary }}>
          No es visible de forma directa. El paciente debe solicitar verla al tocar la tarjeta.
        </span>
      </div>
    </div>
  );
}

// ─── GuideItem ────────────────────────────────────────────────────────────────
function GuideItem({ label, hint, theme }: { readonly label: string; readonly hint: string; readonly theme: any }) {
  return (
    <div style={styles.guideItem}>
      <div style={{ ...styles.guideDot, backgroundColor: theme.primary }} />
      <div style={styles.guideItemText}>
        <span style={{ ...styles.guideItemLabel, color: theme.text }}>{label}</span>
        <span style={{ ...styles.guideItemHint, color: theme.textSecondary }}>{hint}</span>
      </div>
    </div>
  );
}

// ─── MiConsultorioFaqScreen ───────────────────────────────────────────────────
export function MiConsultorioFaqScreen() {
  const theme = useRoleStore(s => s.getTheme());
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profesionalId, setProfesionalId] = useState<string | null>(null);

  // Toast State
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3500);
  };

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) { setLoading(false); return; }
      setProfesionalId(uid);
      const { data } = await supabase
        .from('profesional_servicios_faq')
        .select('id, titulo_opcion, contenido_respuesta, precio_sugerido, moneda, dispara_agendamiento')
        .eq('profesional_uuid', uid)
        .order('id', { ascending: true });
      if (data) {
        setItems(data.map(row => ({
          ...row,
          precio_sugerido: row.precio_sugerido == null ? '' : String(row.precio_sugerido).replace('.', ','),
          moneda: row.moneda || 'USD',
          isDirty: false
        })) as FaqItem[]);
      }
      setLoading(false);
    };
    init();
  }, []);

  const handleAdd = () => {
    if (items.length >= MAX_ITEMS) {
      globalThis.alert(`Límite alcanzado\n\nPuedes tener hasta ${MAX_ITEMS} tarjetas de información. Elimina una antes de agregar otra.`);
      return;
    }
    const tempId = -(Date.now());
    setItems(prev => [...prev, { id: tempId, ...EMPTY_ITEM() }]);
  };

  const handleChange = useCallback((id: number, updated: FaqItem) => {
    setItems(prev => prev.map(it => (it.id === id ? updated : it)));
  }, []);

  const handleDelete = useCallback(async (item: FaqItem) => {
    if (item.isNew) {
      setItems(prev => prev.filter(it => it.id !== item.id));
      return;
    }
    if (!globalThis.confirm('¿Eliminar esta tarjeta? Esta acción no se puede deshacer.')) return;
    setSaving(true);
    const { error } = await supabase.from('profesional_servicios_faq').delete().eq('id', item.id);
    if (error) {
      globalThis.alert('No se pudo eliminar la tarjeta. Intenta de nuevo.');
    } else {
      setItems(prev => prev.filter(it => it.id !== item.id));
    }
    setSaving(false);
  }, []);

  const validateItems = (itemsToValidate: FaqItem[]): boolean => {
    // Regex simplificado para reducir complejidad SonarLint (max 20): atrapa números móviles y fijos VE con/sin 0, +58, guiones o espacios.
    const phoneRegex = /\b(?:0|\+58\s?)?[24]\d{2}[-.\s]*\d{3}[-.\s]*\d{4}\b/;
    for (const it of itemsToValidate) {
      if (!it.titulo_opcion.trim()) {
        globalThis.alert('Todas las tarjetas deben tener un título.');
        return false;
      }
      if (!it.contenido_respuesta.trim()) {
        globalThis.alert(`La tarjeta "${it.titulo_opcion}" no tiene descripción.`);
        return false;
      }
      if (phoneRegex.test(it.contenido_respuesta) || phoneRegex.test(it.titulo_opcion)) {
        globalThis.alert(
          'Para evitar que estafadores y bots extraigan tu número y realicen llamadas de spam o extorsión, evitamos que añadas el número telefónico en las tarjetas de información.'
        );
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!profesionalId) return;
    if (!validateItems(items)) return;
    setSaving(true);
    try {
      const toInsert = items.filter(it => it.isNew).map(it => ({
        profesional_uuid: profesionalId,
        titulo_opcion: it.titulo_opcion.trim(),
        contenido_respuesta: it.contenido_respuesta.trim(),
        precio_sugerido: it.precio_sugerido ? Number.parseFloat(it.precio_sugerido.replace(',', '.')) : null,
        moneda: it.moneda,
        dispara_agendamiento: it.dispara_agendamiento,
      }));

      const toUpdate = items.filter(it => !it.isNew && it.isDirty).map(it => ({
        id: it.id,
        titulo_opcion: it.titulo_opcion.trim(),
        contenido_respuesta: it.contenido_respuesta.trim(),
        precio_sugerido: it.precio_sugerido ? Number.parseFloat(it.precio_sugerido.replace(',', '.')) : null,
        moneda: it.moneda,
        dispara_agendamiento: it.dispara_agendamiento,
      }));

      if (toInsert.length > 0) {
        const { data: inserted, error: insError } = await supabase
          .from('profesional_servicios_faq').insert(toInsert)
          .select('id, titulo_opcion, contenido_respuesta, precio_sugerido, moneda, dispara_agendamiento');
        if (insError) throw insError;
        if (inserted) {
          setItems(prev => {
            const newOnes = prev.filter(it => it.isNew);
            const existing = prev.filter(it => !it.isNew);
            const merged = newOnes.map((it, idx) => ({ ...it, id: inserted[idx]?.id ?? it.id, isNew: false, isDirty: false }));
            return [...existing, ...merged];
          });
        }
      }

      for (const row of toUpdate) {
        const { error: updError } = await supabase.from('profesional_servicios_faq').update({
          titulo_opcion: row.titulo_opcion,
          contenido_respuesta: row.contenido_respuesta,
          precio_sugerido: row.precio_sugerido,
          moneda: row.moneda,
          dispara_agendamiento: row.dispara_agendamiento,
        }).eq('id', row.id);
        if (updError) throw updError;
      }

      setItems(prev => prev.map(it => ({ ...it, isDirty: false, isNew: false })));
      showToast('Los cambios en tu perfil ya son visibles para los pacientes.');
    } catch (err: any) {
      globalThis.alert(err?.message ?? 'Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const hasDirtyItems = items.some(it => it.isDirty || it.isNew);

  if (loading) {
    return (
      <ProfessionalLayout>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <div style={styles.spinner} />
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <div style={{ ...styles.root, backgroundColor: theme.background, overflowY: 'auto' }}>
        <div style={styles.scrollContent}>

          {/* Cabecera */}
          <div style={styles.pageHeader}>
            <h1 style={{ ...styles.pageTitle, color: theme.text }}>Información de mi perfil</h1>
            <p style={{ ...styles.pageSubtitle, color: theme.textSecondary }}>
              Los pacientes verán estas tarjetas en tu perfil público al buscar un profesional.
              Cada tarjeta se despliega al ser tocada y genera interés sin saturar con texto.
            </p>
          </div>

          {/* Banner límite */}
          <div style={{ ...styles.limitBanner, borderColor: '#E2E8F0' }}>
            <Info size={16} color="#64748B" />
            <span style={{ ...styles.limitBannerText, color: theme.textSecondary }}>
              {items.length} de {MAX_ITEMS} tarjetas utilizadas
            </span>
          </div>

          {/* Editors */}
          {items.map((item, index) => (
            <FaqEditor
              key={item.id}
              item={item}
              index={index}
              onChange={updated => handleChange(item.id, updated)}
              onDelete={() => handleDelete(item)}
              saving={saving}
            />
          ))}

          {/* Agregar */}
          {items.length < MAX_ITEMS && (
            <button
              style={{ ...styles.addBtn, borderColor: theme.primary }}
              onClick={handleAdd}
              disabled={saving}
            >
              <PlusCircle size={20} color={theme.primary} />
              <span style={{ ...styles.addBtnText, color: theme.primary }}>Agregar tarjeta</span>
            </button>
          )}

          {/* Guía */}
          <div style={{ ...styles.guideBlock, borderColor: '#E2E8F0', backgroundColor: theme.surface }}>
            <span style={{ ...styles.guideTitle, color: theme.text }}>Guía de contenido efectivo</span>
            <GuideItem label="Servicios que ofreces" hint="Lista las áreas en que puedes ayudar. Sin tecnicismos. (Máx 200 caracteres)" theme={theme} />
            <GuideItem label="Modalidad de atención" hint="Presencial, videollamada, domicilio. Breve y claro." theme={theme} />
            <GuideItem label="Detalles de la sesión" hint="Duración, enfoque, qué esperar en la primera consulta." theme={theme} />
            <GuideItem label="Tarifas" hint="Muestra directamente el precio de tu servicio al paciente de forma clara y sin ambigüedades." theme={theme} />
          </div>

          <div style={{ height: '120px' }} />
        </div>
      </div>

      {/* CTA Flotante */}
      {hasDirtyItems && (
        <div style={{ ...styles.footerCTA, backgroundColor: theme.surface }}>
          <button
            style={{ ...styles.saveBtn, backgroundColor: theme.primary, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <div style={styles.spinnerWhite} />
            ) : (
              <>
                <CheckCircle size={20} color="#FFFFFF" />
                <span style={styles.saveBtnText}>Guardar cambios</span>
              </>
            )}
          </button>
          <span style={{ ...styles.saveHint, color: theme.textSecondary }}>Tienes cambios sin guardar</span>
        </div>
      )}

      {/* Toast */}
      {toastVisible && (
        <div style={{
          position: 'fixed', bottom: '100px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#1F2937', padding: '12px 20px', borderRadius: '24px',
          display: 'flex', flexDirection: 'row', alignItems: 'center',
          boxShadow: '0px 4px 10px rgba(0,0,0,0.2)', zIndex: 9999, animation: 'fadeIn 0.3s ease'
        }}>
          <CheckCircle size={20} color="#FFF" style={{ marginRight: '8px' }} />
          <span style={{ color: '#FFF', fontSize: '14px', fontWeight: 600 }}>{toastMessage}</span>
        </div>
      )}
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } } * { box-sizing: border-box; } textarea,input { outline: none; }`}</style>
    </ProfessionalLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: { flex: 1 },
  scrollContent: { padding: '20px', maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column' },
  pageHeader: { marginBottom: '20px' },
  pageTitle: { fontSize: '22px', fontWeight: 800, marginBottom: '8px', margin: '0 0 8px 0' },
  pageSubtitle: { fontSize: '14px', lineHeight: '22px', margin: 0 },
  limitBanner: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', border: '1px solid', marginBottom: '20px', backgroundColor: '#F8FAFC' },
  limitBannerText: { fontSize: '13px', fontWeight: 500 },
  card: { borderRadius: '16px', border: '1px solid', padding: '16px', marginBottom: '16px', backgroundColor: '#FFFFFF' },
  cardHeader: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  cardIndexBadge: { width: '28px', height: '28px', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  cardIndexText: { fontSize: '13px', fontWeight: 700 },
  cardHeaderLabel: { flex: 1, fontSize: '13px', fontWeight: 600 },
  deleteBtn: { padding: '4px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  fieldBlock: { marginBottom: '14px', display: 'flex', flexDirection: 'column' },
  fieldLabelRow: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  fieldLabel: { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  fieldOptional: { fontSize: '11px', fontStyle: 'italic' },
  fieldHint: { fontSize: '11px', marginTop: '5px', lineHeight: '16px', display: 'block' },
  input: { border: '1px solid', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', backgroundColor: '#FAFBFC', width: '100%' },
  inputMultiline: { minHeight: '80px', resize: 'vertical' },
  inputPrice: { width: '160px', fontWeight: 600, fontSize: '16px' },
  addBtn: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', borderRadius: '12px', border: '1.5px dashed', marginBottom: '24px', background: 'none', cursor: 'pointer', transition: 'opacity 0.2s', width: '100%' },
  addBtnText: { fontSize: '14px', fontWeight: 600 },
  guideBlock: { borderRadius: '14px', border: '1px solid', padding: '16px', marginBottom: '12px', display: 'flex', flexDirection: 'column' },
  guideTitle: { fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px', display: 'block' },
  guideItem: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' },
  guideDot: { width: '6px', height: '6px', borderRadius: '3px', marginTop: '5px', flexShrink: 0 },
  guideItemText: { flex: 1, display: 'flex', flexDirection: 'column' },
  guideItemLabel: { fontSize: '13px', fontWeight: 600, marginBottom: '2px' },
  guideItemHint: { fontSize: '12px', lineHeight: '18px' },
  footerCTA: { position: 'fixed', bottom: 0, left: 0, right: 0, padding: '14px 20px 28px 20px', borderTop: '1px solid #E2E8F0', boxShadow: '0px -4px 12px rgba(0,0,0,0.06)', zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  saveBtn: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', borderRadius: '100px', border: 'none', width: '100%', maxWidth: '500px' },
  saveBtnText: { color: '#FFFFFF', fontSize: '15px', fontWeight: 700 },
  saveHint: { textAlign: 'center', fontSize: '12px', marginTop: '6px' },
  spinner: { width: '36px', height: '36px', border: '4px solid #0052CC', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerWhite: { width: '22px', height: '22px', border: '3px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
